import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Icon, type IconProps } from "@project/ui";
import { cn } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useActiveHouse } from "@apps/dashboard/features/houses/active-house-context";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { SignOutButton } from "@apps/dashboard/components/auth/SignOutButton";
import { BrandWordmark } from "@apps/dashboard/components/brand/BrandWordmark";

type NavIcon = IconProps["name"];

interface NavItem {
  to: string;
  end?: boolean;
  labelKey: string;
  icon: NavIcon;
  /** Ancla para tour del panel (driver.js). */
  dataTour?: string;
}

function navItems(paths: {
  home: string;
  journal: string;
  financings: string;
  budget: string;
  analytics: string;
  vault: string;
  scan: string;
}): NavItem[] {
  return [
    {
      to: paths.home,
      end: true,
      labelKey: "dashboardUi.nav.dashboard",
      icon: "LayoutDashboard",
      dataTour: "sidebar-home",
    },
    {
      to: paths.journal,
      labelKey: "dashboardUi.nav.journal",
      icon: "BookOpen",
      dataTour: "nav-journal",
    },
    {
      to: paths.financings,
      labelKey: "dashboardUi.nav.financings",
      icon: "Landmark",
    },
    { to: dashboardPaths.search, labelKey: "dashboardUi.nav.search", icon: "Search" },
    {
      to: paths.budget,
      labelKey: "dashboardUi.nav.budget",
      icon: "Wallet",
      dataTour: "nav-budget",
    },
    {
      to: paths.analytics,
      labelKey: "dashboardUi.nav.analytics",
      icon: "LineChart",
    },
    { to: paths.vault, labelKey: "dashboardUi.nav.vault", icon: "Lock" },
    { to: paths.scan, labelKey: "dashboardUi.nav.scan", icon: "ScanLine" },
    {
      to: dashboardPaths.houses,
      end: true,
      labelKey: "dashboardUi.nav.houses",
      icon: "Home",
      dataTour: "nav-houses",
    },
    { to: dashboardPaths.settings, labelKey: "dashboardUi.nav.accountData", icon: "User" },
  ];
}

function NavBlock({
  items,
  onNavigate,
  className,
}: {
  items: NavItem[];
  onNavigate?: () => void;
  className?: string;
}): JSX.Element {
  const { t } = useI18n();

  return (
    <nav className={cn("flex flex-col gap-2", className)} aria-label={t("dashboardUi.nav.mainNavigation")}>
      {items.map(({ to, end, labelKey, icon, dataTour }) => (
        <NavLink
          key={labelKey}
          to={to}
          end={end}
          onClick={onNavigate}
          {...(dataTour ? { "data-tour": dataTour } : {})}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-xl px-4 py-3 font-label text-label-md transition-all",
              isActive
                ? "translate-x-1 border-r-4 border-primary bg-surface-container-lowest/80 font-bold text-primary shadow-sm outline outline-1 outline-outline-variant/10"
                : "text-on-surface-variant hover:bg-surface-container-low"
            )
          }
        >
          <Icon name={icon} className="size-6 shrink-0" strokeWidth={1.5} aria-hidden />
          <span>{t(labelKey)}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export function AppSidebar(): JSX.Element {
  const { t } = useI18n();
  const { activeHouseId } = useActiveHouse();
  const h = activeHouseId;
  const items = navItems({
    home: dashboardPaths.home,
    journal: h ? dashboardPaths.houseExpenses(h) : dashboardPaths.houses,
    financings: h ? dashboardPaths.houseFinancings(h) : dashboardPaths.houses,
    budget: h ? dashboardPaths.houseBudget(h) : dashboardPaths.houses,
    analytics: h ? dashboardPaths.houseAnalytics(h) : dashboardPaths.houses,
    vault: h ? dashboardPaths.houseVault(h) : dashboardPaths.houses,
    scan: h ? dashboardPaths.houseScan(h) : dashboardPaths.houses,
  });
  const expenseNewTo = h ? dashboardPaths.houseExpenseNew(h) : dashboardPaths.houses;

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-30 hidden h-screen w-64 flex-col overflow-y-auto py-8 pl-6 pr-4 lg:flex",
        "bg-surface-container"
      )}
    >
      <div className="mb-10">
        <BrandWordmark textClassName="text-lg font-black" />
        <p className="text-label-md mt-2 pl-0.5 text-on-surface-variant">{t("dashboardUi.shell.brandTagline")}</p>
      </div>
      <NavBlock items={items} className="flex-grow" />
      <div className="mt-auto flex flex-col gap-2 border-t border-outline-variant/15 pt-8">
        <Link
          to={expenseNewTo}
          data-tour="sidebar-add-expense"
          className={cn(
            "font-headline mb-4 w-full rounded-xl py-4 text-center text-sm font-bold text-on-primary shadow-lg",
            "bg-gradient-to-br from-primary to-primary-container",
            "active:scale-[0.98] transition-transform"
          )}
        >
          {t("dashboardUi.shell.addExpense")}
        </Link>
        <NavLink
          to={dashboardPaths.support}
          className={cn(
            "flex items-center gap-3 rounded-lg px-4 py-2 font-label text-label-md text-on-surface-variant transition-colors hover:text-primary"
          )}
        >
          <Icon name="HelpCircle" className="size-4 shrink-0" strokeWidth={1.5} />
          {t("dashboardUi.nav.support")}
        </NavLink>
        <SignOutButton />
      </div>
    </aside>
  );
}

export function AppMobileDrawer(): JSX.Element {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const { activeHouseId } = useActiveHouse();
  const h = activeHouseId;
  const items = navItems({
    home: dashboardPaths.home,
    journal: h ? dashboardPaths.houseExpenses(h) : dashboardPaths.houses,
    financings: h ? dashboardPaths.houseFinancings(h) : dashboardPaths.houses,
    budget: h ? dashboardPaths.houseBudget(h) : dashboardPaths.houses,
    analytics: h ? dashboardPaths.houseAnalytics(h) : dashboardPaths.houses,
    vault: h ? dashboardPaths.houseVault(h) : dashboardPaths.houses,
    scan: h ? dashboardPaths.houseScan(h) : dashboardPaths.houses,
  });
  const expenseNewTo = h ? dashboardPaths.houseExpenseNew(h) : dashboardPaths.houses;

  return (
    <>
      <div
        className={cn(
          "sticky top-0 z-20 flex items-center justify-between bg-surface px-4 py-3 lg:hidden",
          "outline outline-1 outline-outline-variant/15 outline-offset-[-1px]"
        )}
      >
        <BrandWordmark markClassName="size-8" textClassName="text-sm font-bold" />
        <button
          type="button"
          className="rounded-lg p-2 text-on-surface-variant hover:bg-surface-container-low"
          aria-expanded={open}
          aria-controls="mobile-dashboard-nav"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="sr-only">{t("dashboardUi.mobile.openMenu")}</span>
          <Icon name={open ? "X" : "Menu"} className="size-6" strokeWidth={1.5} />
        </button>
      </div>
      {open ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-on-surface/20 lg:hidden"
          aria-label={t("common.close")}
          onClick={() => setOpen(false)}
        />
      ) : null}
      <aside
        id="mobile-dashboard-nav"
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 max-w-[85vw] transform bg-surface-container py-8 pl-6 pr-4 shadow-xl transition-transform duration-200 lg:hidden",
          open ? "translate-x-0" : "-translate-x-full pointer-events-none"
        )}
      >
        <NavBlock items={items} onNavigate={() => setOpen(false)} className="flex-grow" />
        <div className="mt-8 flex flex-col gap-2 border-t border-outline-variant/15 pt-6">
          <Link
            to={expenseNewTo}
            data-tour="sidebar-add-expense"
            onClick={() => setOpen(false)}
            className={cn(
              "font-headline w-full rounded-xl py-3 text-center text-sm font-bold text-on-primary",
              "bg-gradient-to-br from-primary to-primary-container"
            )}
          >
            {t("dashboardUi.shell.addExpense")}
          </Link>
          <NavLink
            to={dashboardPaths.support}
            onClick={() => setOpen(false)}
            className={cn(
              "flex items-center gap-3 rounded-lg px-4 py-2 font-label text-label-md text-on-surface-variant transition-colors hover:text-primary"
            )}
          >
            <Icon name="HelpCircle" className="size-4 shrink-0" strokeWidth={1.5} />
            {t("dashboardUi.nav.support")}
          </NavLink>
          <SignOutButton onDone={() => setOpen(false)} className="mt-2 justify-center" />
        </div>
      </aside>
    </>
  );
}
